import * as z from 'zod'
import {iconColors} from '@primer/react-brand/esm'
import {buildEntrySchemaFor} from '../entry'
import {RichTextSchema} from '../richText'
import {PrimerComponentLabelSchema} from './primerComponentLabel'
import {LinkSchema} from './link'
import {safeOptional} from '../../safe-optional'

export const PrimerComponentPricingOptionsFeatureListItemSchema = buildEntrySchemaFor(
  'primerComponentPricingOptionsListItem',
  {
    fields: z.lazy(() =>
      z.object({
        variant: z.enum(['included', 'excluded']).optional(),
        icon: z.string().optional(),
        iconColor: z.enum(iconColors).optional(),
        infoTooltip: z.string().optional(),
        infoTooltipAriaLabel: z.string().optional(),
        description: RichTextSchema,
      }),
    ),
  },
)
export type PrimerComponentPricingOptionsFeatureListItem = z.infer<
  typeof PrimerComponentPricingOptionsFeatureListItemSchema
>

export const PrimerComponentPricingOptionsFeatureListGroupHeadingSchema = buildEntrySchemaFor(
  'primerComponentPricingOptionsListHeading',
  {
    fields: z.lazy(() =>
      z.object({
        heading: RichTextSchema,
        headingLevel: z.enum(['h3', 'h4', 'h5', 'h6']).optional(),
      }),
    ),
  },
)
export type PrimerComponentPricingOptionsFeatureListGroupHeading = z.infer<
  typeof PrimerComponentPricingOptionsFeatureListGroupHeadingSchema
>

export const PrimerComponentPricingOptionsFeatureListHeadingSchema = buildEntrySchemaFor(
  'primerComponentPricingItemListHeading',
  {
    fields: z.lazy(() =>
      z.object({
        heading: RichTextSchema,
      }),
    ),
  },
)
export type PrimerComponentPricingOptionsFeatureListHeading = z.infer<
  typeof PrimerComponentPricingOptionsFeatureListHeadingSchema
>

export const PrimerComponentPricingOptionsItemSchema = buildEntrySchemaFor('primerComponentPricingOptionsItem', {
  fields: z.lazy(() =>
    z.object({
      heading: RichTextSchema,
      headingLevel: z.enum(['h2', 'h3', 'h4', 'h5', 'h6']).optional(),
      description: safeOptional(RichTextSchema),
      footnote: safeOptional(RichTextSchema),
      accordionHeadingLevel: z.enum(['h2', 'h3', 'h4', 'h5', 'h6']).optional(),
      featureList: safeOptional(
        z.array(
          z.union([
            PrimerComponentPricingOptionsFeatureListItemSchema,
            PrimerComponentPricingOptionsFeatureListGroupHeadingSchema,
            PrimerComponentPricingOptionsFeatureListHeadingSchema,
          ]),
        ),
      ),
      featureListExpanded: z.boolean().optional(),
      featureListHasDivider: z.boolean().optional(),
      label: safeOptional(PrimerComponentLabelSchema),
      currentPrice: RichTextSchema,
      currencyCode: z.string().optional(),
      currencySymbol: z.string().optional(),
      originalPrice: z.string().optional(),
      priceTrailingText: z.string().optional(),
      callToActionPrimary: safeOptional(LinkSchema),
      callToActionPrimaryVariant: z.enum(['accent', 'primary']).optional(),
      callToActionSecondary: safeOptional(LinkSchema),
      callToActionSecondaryVariant: z.enum(['subtle', 'secondary']).optional(),
      actionsMessage: safeOptional(RichTextSchema),
      hideCtas: z.boolean().optional(),
    }),
  ),
})
export type PrimerComponentPricingOptionsItem = z.infer<typeof PrimerComponentPricingOptionsItemSchema>

export const PrimerComponentPricingOptionsSchema = buildEntrySchemaFor('primerComponentPricingOptions', {
  fields: z.lazy(() =>
    z.object({
      align: z.enum(['start', 'center']).optional(),
      variant: z.enum(['default', 'cards', 'default-gradient', 'cards-gradient']).optional(),
      items: z.array(PrimerComponentPricingOptionsItemSchema),
    }),
  ),
})
export type PrimerComponentPricingOptions = z.infer<typeof PrimerComponentPricingOptionsSchema>
